import { CancelFunctions } from 'components/common/types';

export type PollingOptions<T> = {
  poll: () => Promise<T>;
  shouldStop: (result: T) => boolean;
  attemptsPerInterval?: number;
  initialInterval?: number;
  intervalIncrement?: number;
  maxInterval?: number;
  timeout?: number;
  signal?: AbortSignal;
  cancelFunctions?: CancelFunctions;
  cancelKey?: string;
  requestCancelKeys?: string[];
};

export class PollingError extends Error {
  constructor(
    message: string,
    readonly code: 'timeout' | 'aborted',
  ) {
    super(message);
    this.name = code === 'timeout' ? 'PollingTimeoutError' : 'PollingAbortedError';
  }
}

const ATTEMPTS_PER_INTERVAL = 1;
const DEFAULT_INITIAL_INTERVAL = 1000;
const DEFAULT_INTERVAL_INCREMENT = 1000;
const DEFAULT_MAX_INTERVAL = 5000;

const abortedError = (): PollingError => new PollingError('Polling aborted', 'aborted');

const registerPollingCancel = ({
  signal,
  cancelFunctions,
  cancelKey,
  requestCancelKeys,
}: Pick<PollingOptions<unknown>, 'signal' | 'cancelFunctions' | 'cancelKey' | 'requestCancelKeys'>):
  | AbortSignal
  | undefined => {
  if (!cancelFunctions && !cancelKey) {
    return signal;
  }

  if (!cancelFunctions || !cancelKey) {
    throw new Error('cancelFunctions and cancelKey must be provided together');
  }

  const controller = new AbortController();

  const cancelPolling = (): void => {
    controller.abort();
    requestCancelKeys?.forEach((key) => {
      if (key !== cancelKey) {
        cancelFunctions[key]?.();
      }
    });
  };

  cancelFunctions[cancelKey]?.();
  cancelFunctions[cancelKey] = cancelPolling;

  if (signal) {
    if (signal.aborted) {
      cancelPolling();
    } else {
      signal.addEventListener('abort', cancelPolling, { once: true });
    }
  }

  return controller.signal;
};

const throwIfAborted = (signal?: AbortSignal): void => {
  if (signal?.aborted) {
    throw abortedError();
  }
};

const wait = (timeout: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortedError());

      return;
    }

    let timeoutId: ReturnType<typeof setTimeout>;

    const onAbort = (): void => {
      clearTimeout(timeoutId);
      reject(abortedError());
    };

    timeoutId = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, timeout);

    signal?.addEventListener('abort', onAbort, { once: true });
  });

export const pollingWithBackoff = async <T>({
  poll,
  shouldStop,
  attemptsPerInterval = ATTEMPTS_PER_INTERVAL,
  initialInterval = DEFAULT_INITIAL_INTERVAL,
  intervalIncrement = DEFAULT_INTERVAL_INCREMENT,
  maxInterval = DEFAULT_MAX_INTERVAL,
  timeout,
  signal: externalSignal,
  cancelFunctions,
  cancelKey,
  requestCancelKeys,
}: PollingOptions<T>): Promise<T> => {
  const signal = registerPollingCancel({
    signal: externalSignal,
    cancelFunctions,
    cancelKey,
    requestCancelKeys,
  });

  const startedAt = Date.now();

  const throwIfTimedOut = (): void => {
    if (timeout !== undefined && Date.now() - startedAt >= timeout) {
      throw new PollingError('Polling timed out', 'timeout');
    }
  };

  const getWaitMs = (interval: number): number => {
    if (timeout === undefined) {
      return interval;
    }

    const elapsed = Date.now() - startedAt;

    return Math.min(interval, Math.max(timeout - elapsed, 0));
  };

  const attempt = async (interval: number, attemptCount: number): Promise<T> => {
    throwIfAborted(signal);

    let result: T;

    try {
      result = await poll();
    } catch (error) {
      throwIfAborted(signal);
      throw error;
    }

    throwIfAborted(signal);

    if (shouldStop(result)) {
      return result;
    }

    const nextAttemptCount = attemptCount + 1;
    const nextInterval =
      nextAttemptCount % attemptsPerInterval === 0 ? Math.min(interval + intervalIncrement, maxInterval) : interval;

    throwIfAborted(signal);
    throwIfTimedOut();

    await wait(getWaitMs(interval), signal);

    return attempt(nextInterval, nextAttemptCount);
  };

  return attempt(initialInterval, 0);
};
